import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { landingApi } from '../../features/landing/api/landingApi';
import type { LandingPageData } from '../../features/landing/types';
import LandingHero from '../../features/landing/components/LandingHero';
import LandingStats from '../../features/landing/components/LandingStats';
import LandingSections from '../../features/landing/components/LandingSections';
import LandingFooter from '../../features/landing/components/LandingFooter';
import QuickActionCards from '../../components/home/QuickActionCards';
import GettingStartedSection from '../../features/landing/components/GettingStartedSection';
import AppsSection from '../../features/landing/components/AppsSection';
import CanvasBackgroundLayer from '../../features/landing/components/CanvasBackgroundLayer';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';

const HomeLandingPage: React.FC = () => {
    const { t } = useTranslation();
    const [data, setData] = useState<LandingPageData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await landingApi.getHomeContent();
            setData(response);
        } catch (err) {
            setError(t('landing.error', 'Failed to load content.'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center">
                <Skeleton className="w-full max-w-2xl h-16 mb-4 bg-slate-800" />
                <Skeleton className="w-full max-w-xl h-8 mb-10 bg-slate-800" />
                <div className="flex gap-4">
                    <Skeleton className="w-40 h-12 bg-slate-800" />
                    <Skeleton className="w-40 h-12 bg-slate-800" />
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-4">
                <div className="text-6xl mb-4">⚠️</div>
                <h2 className="text-2xl font-bold text-white mb-4">Something went wrong</h2>
                <p className="text-slate-400 mb-8">{error}</p>
                <Button onClick={fetchData} className="bg-violet-600 hover:bg-violet-500">
                    Try Again
                </Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            {/* Top Section - Dark background with grid dots */}
            <section className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-slate-950">
                {/* Dark gradient background */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-900/20 via-black to-black" />
                
                {/* Grid dots pattern */}
                <div className="absolute inset-0 opacity-20">
                    <CanvasBackgroundLayer className="z-0" />
                </div>

                {/* Hero Content */}
                <div className="relative z-10 w-full">
                    <LandingHero data={data.hero} />
                </div>
            </section>

            {/* Middle Section - Light gradient with Getting Started */}
            <GettingStartedSection />

            {/* Bottom Section - Dark background with Apps */}
            <AppsSection />

            {/* Additional sections (optional - can be removed if not needed) */}
            <QuickActionCards />
            <LandingStats data={data.stats} />
            <LandingSections sections={data.sections} />
            <LandingFooter data={data.footer} />
        </div>
    );
};

export default HomeLandingPage;
